import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import {
  User, BookOpen, Monitor, CreditCard, LogOut, Clock,
  Layers, ChevronRight, Lock, Play, Award, Settings, Package, Shield
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Same catalog data mapping for course details
const LEVEL_PRICES = { 'Débutant': 12000, 'Intermédiaire': 18000, 'Avancé': 22000 };

const formatPrice = (price) => new Intl.NumberFormat('fr-FR').format(price) + ' XPF';

const EspaceClientPage = () => {
  const { user, token, logout, refreshUser, loading } = useAuth();
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('dashboard');
  const [purchases, setPurchases] = useState([]);
  const [availableCourses, setAvailableCourses] = useState([]);
  const [profileForm, setProfileForm] = useState({ name: '', phone: '', company: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!loading && !user) {
      navigate('/formations?showLogin=true');
    }
  }, [user, loading, navigate]);

  useEffect(() => {
    if (user) {
      setProfileForm({ name: user.name || '', phone: user.phone || '', company: user.company || '' });
      fetchPurchases();
      fetchAvailableCourses();
    }
  }, [user]);

  const fetchPurchases = async () => {
    try {
      const res = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPurchases(res.data.purchases || []);
    } catch {}
  };

  const fetchAvailableCourses = async () => {
    try {
      const res = await axios.get(`${API}/courses`);
      setAvailableCourses(res.data || []);
    } catch {}
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(`${API}/auth/profile`, profileForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      refreshUser();
      toast.success('Profil mis à jour');
    } catch {
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-teal-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!user) return null;

  const sidebarItems = [
    { id: 'dashboard', label: 'Tableau de bord', icon: Monitor },
    { id: 'courses', label: 'Mes formations', icon: BookOpen },
    { id: 'purchases', label: 'Mes achats', icon: CreditCard },
    { id: 'profile', label: 'Mon profil', icon: Settings },
  ];

  return (
    <div className="min-h-screen bg-slate-50 pt-24 pb-16" data-testid="espace-client">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-slate-900">Espace Client</h1>
          <p className="text-slate-500">Bienvenue, {user.name}</p>
        </div>

        <div className="grid lg:grid-cols-[260px_1fr] gap-8">
          {/* Sidebar */}
          <div className="bg-white rounded-xl border border-slate-200 p-4 h-fit" data-testid="sidebar">
            <div className="flex items-center gap-3 p-3 mb-4 bg-slate-50 rounded-lg">
              <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center">
                <User className="w-5 h-5 text-teal-600" />
              </div>
              <div>
                <p className="font-semibold text-sm text-slate-900">{user.name}</p>
                <p className="text-xs text-slate-500">{user.email}</p>
              </div>
            </div>
            <nav className="space-y-1">
              {sidebarItems.map(item => (
                <button
                  key={item.id}
                  onClick={() => setActiveSection(item.id)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                    activeSection === item.id
                      ? 'bg-teal-50 text-teal-700'
                      : 'text-slate-600 hover:bg-slate-50'
                  }`}
                  data-testid={`nav-${item.id}`}
                >
                  <item.icon className="w-4 h-4" />
                  {item.label}
                </button>
              ))}
              <button
                onClick={() => { logout(); navigate('/'); }}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-red-600 hover:bg-red-50 transition-colors"
                data-testid="nav-logout"
              >
                <LogOut className="w-4 h-4" />
                Déconnexion
              </button>
            </nav>
          </div>

          {/* Main Content */}
          <div className="space-y-6">
            {/* Dashboard */}
            {activeSection === 'dashboard' && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="section-dashboard">
                <div className="grid sm:grid-cols-3 gap-4 mb-6">
                  <div className="bg-white border border-slate-200 rounded-xl p-6">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 rounded-lg bg-teal-50 flex items-center justify-center">
                        <BookOpen className="w-5 h-5 text-teal-600" />
                      </div>
                      <span className="text-2xl font-bold text-slate-900">{user.purchased_courses?.length || 0}</span>
                    </div>
                    <p className="text-sm text-slate-500">Formations achetées</p>
                  </div>
                  <div className="bg-white border border-slate-200 rounded-xl p-6">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center">
                        <Award className="w-5 h-5 text-amber-600" />
                      </div>
                      <span className="text-2xl font-bold text-slate-900">0</span>
                    </div>
                    <p className="text-sm text-slate-500">Certificats obtenus</p>
                  </div>
                  <div className="bg-white border border-slate-200 rounded-xl p-6">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                        <Clock className="w-5 h-5 text-blue-600" />
                      </div>
                      <span className="text-2xl font-bold text-slate-900">0h</span>
                    </div>
                    <p className="text-sm text-slate-500">Temps de formation</p>
                  </div>
                </div>

                {/* Quick actions */}
                <div className="bg-white border border-slate-200 rounded-xl p-6">
                  <h3 className="font-bold text-slate-900 mb-4">Actions rapides</h3>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <Button
                      variant="outline"
                      className="justify-start h-auto p-4"
                      onClick={() => navigate('/formations')}
                      data-testid="btn-browse-catalog"
                    >
                      <Package className="w-5 h-5 mr-3 text-teal-600" />
                      <div className="text-left">
                        <p className="font-medium">Catalogue E-Learning</p>
                        <p className="text-xs text-slate-500">Découvrir les 28 formations</p>
                      </div>
                    </Button>
                    <Button
                      variant="outline"
                      className="justify-start h-auto p-4"
                      onClick={() => setActiveSection('courses')}
                      data-testid="btn-my-courses"
                    >
                      <Play className="w-5 h-5 mr-3 text-teal-600" />
                      <div className="text-left">
                        <p className="font-medium">Mes formations</p>
                        <p className="text-xs text-slate-500">Acceder à vos cours</p>
                      </div>
                    </Button>
                  </div>
                </div>
              </motion.div>
            )}

            {/* My Courses */}
            {activeSection === 'courses' && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="section-courses">
                <h2 className="text-xl font-bold text-slate-900 mb-4">Mes formations</h2>
                {(user.purchased_courses?.length || 0) === 0 ? (
                  <div className="bg-white border border-slate-200 rounded-xl p-12 text-center">
                    <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
                      <Lock className="w-8 h-8 text-slate-400" />
                    </div>
                    <h3 className="text-lg font-bold text-slate-900 mb-2">Aucune formation achetee</h3>
                    <p className="text-slate-500 mb-6">Explorez notre catalogue de 28 formations e-learning professionnelles.</p>
                    <Button onClick={() => navigate('/formations')} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="btn-explore-catalog">
                      <BookOpen className="w-4 h-4 mr-2" /> Explorer le catalogue
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {availableCourses.length > 0 ? (
                      availableCourses.map((course) => (
                        <div key={course.catalog_id} className="bg-white border border-slate-200 rounded-xl p-5 flex items-center gap-4 hover:shadow-md transition-shadow" data-testid={`course-card-${course.catalog_id}`}>
                          <div className="w-12 h-12 rounded-lg bg-teal-50 flex items-center justify-center shrink-0">
                            <Monitor className="w-6 h-6 text-teal-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold text-slate-900 text-sm truncate">{course.title}</p>
                            <p className="text-xs text-slate-500">{course.category} - {course.level} - {course.slide_count} slides</p>
                          </div>
                          <Button
                            size="sm"
                            onClick={() => navigate(`/cours/${course.catalog_id}`)}
                            className="bg-teal-600 hover:bg-teal-700 text-white shrink-0"
                            data-testid={`btn-start-${course.catalog_id}`}
                          >
                            <Play className="w-4 h-4 mr-1" /> Commencer
                          </Button>
                        </div>
                      ))
                    ) : (
                      <div className="bg-white border border-slate-200 rounded-xl p-8 text-center">
                        <Clock className="w-12 h-12 text-amber-400 mx-auto mb-3" />
                        <h3 className="text-lg font-bold text-slate-900 mb-2">Formations en preparation</h3>
                        <p className="text-slate-500 text-sm">Vos formations sont en cours de création. Elles seront bientôt disponibles ici.</p>
                      </div>
                    )}
                  </div>
                )}
              </motion.div>
            )}

            {/* Purchases */}
            {activeSection === 'purchases' && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="section-purchases">
                <h2 className="text-xl font-bold text-slate-900 mb-4">Mes achats</h2>
                {purchases.length === 0 ? (
                  <div className="bg-white border border-slate-200 rounded-xl p-12 text-center">
                    <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
                      <CreditCard className="w-8 h-8 text-slate-400" />
                    </div>
                    <h3 className="text-lg font-bold text-slate-900 mb-2">Aucun achat</h3>
                    <p className="text-slate-500">Vos transactions apparaîtront ici après votre premier achat.</p>
                  </div>
                ) : (
                  <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 border-b border-slate-200">
                        <tr>
                          <th className="text-left p-4 font-semibold text-slate-600">Formation</th>
                          <th className="text-left p-4 font-semibold text-slate-600">Montant</th>
                          <th className="text-left p-4 font-semibold text-slate-600">Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {purchases.map((p, i) => (
                          <tr key={i} className="border-b border-slate-100 last:border-0">
                            <td className="p-4 font-medium text-slate-900">{p.package_title}</td>
                            <td className="p-4 text-teal-700 font-semibold">{p.amount_xpf ? formatPrice(p.amount_xpf) : '-'}</td>
                            <td className="p-4 text-slate-500">{p.created_at ? new Date(p.created_at).toLocaleDateString('fr-FR') : '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </motion.div>
            )}

            {/* Profile */}
            {activeSection === 'profile' && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="section-profile">
                <h2 className="text-xl font-bold text-slate-900 mb-4">Mon profil</h2>
                <div className="bg-white border border-slate-200 rounded-xl p-6">
                  <form onSubmit={handleSaveProfile} className="space-y-4 max-w-md">
                    <div>
                      <Label>Email</Label>
                      <Input value={user.email} disabled className="bg-slate-50" />
                    </div>
                    <div>
                      <Label htmlFor="profile-name">Nom complet</Label>
                      <Input
                        id="profile-name"
                        value={profileForm.name}
                        onChange={e => setProfileForm({ ...profileForm, name: e.target.value })}
                        data-testid="profile-name"
                      />
                    </div>
                    <div>
                      <Label htmlFor="profile-phone">Téléphone</Label>
                      <Input
                        id="profile-phone"
                        value={profileForm.phone}
                        onChange={e => setProfileForm({ ...profileForm, phone: e.target.value })}
                        data-testid="profile-phone"
                      />
                    </div>
                    <div>
                      <Label htmlFor="profile-company">Entreprise</Label>
                      <Input
                        id="profile-company"
                        value={profileForm.company}
                        onChange={e => setProfileForm({ ...profileForm, company: e.target.value })}
                        data-testid="profile-company"
                      />
                    </div>
                    <Button type="submit" disabled={saving} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="btn-save-profile">
                      {saving ? 'Enregistrement...' : 'Enregistrer'}
                    </Button>
                  </form>
                </div>
              </motion.div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EspaceClientPage;
